import { useEffect, useState } from 'react';
import {
  ModalDialogComponent,
  ModalDialogComponentProps,
  GetMessage,
  GetMessageWithIntl,
  error,
  MessageProps,
} from '~/shared/components';

import type { AggregateStage, DummyUser, FullMethodName } from '~/worker';

import {
  InitialFilterItem,
  ListView,
  Preset,
  Property,
  Schema,
  MenuActionItem,
  getSchemaByTypeName,
} from '~/shared/components/ui';
import { PresetItem } from '~/shared/services';
import './base.css';

import { useAuth } from '~/shared/contexts/AuthProvider';
import { useIntl } from 'react-intl';
import {
  ViewId,
  getExceptionMessage,
  getLocalStorageCheckboxData,
  getPresetAndSchema,
  saveInitialLocalStorageData,
} from '~/shared/utils';
import { CaptionButton } from '../Button';

export interface AggregateStageOption {
  aggregateStages: AggregateStage[];
  aggregateType?: string;
  mergedShema?: boolean;
  aggregateFullMethodName: string[];
}

export interface SearchListViewDialogProps {
  fullMethodName: FullMethodName;
  viewId: ViewId;
  isOpen: boolean;
  headerLabelId: MessageProps;
  headerTitleId?: MessageProps;
  selectLimit?: number;
  menuTarget?: string;
  menuEvent?: MenuActionItem[];
  selectedItemIds?: string[];
  aggregateStagesOption?: AggregateStageOption;
  onCloseChange?: () => void;
  onSelectChange?: (items: DummyUser[]) => void;
  onHandleFormatSchema?: (sch: Schema) => Schema;
  initialFilterItems?: { type: string; info: InitialFilterItem[] };
  initialRequestBodyFilterItems?: { type: string; info: InitialFilterItem[] };
}

export function SearchListViewDialog(props: SearchListViewDialogProps) {
  const intl = useIntl();
  // ログイン者の情報特定用email
  const myEmail = useAuth().user?.email ?? '';
  const msg = GetMessage({
    id: 'selectItemOverError',
    value: {
      number: props.selectLimit,
      unit: '個',
    },
  });
  const [schema, setSchema] = useState<Property[]>([]);
  const [preset, setPreset] = useState<Preset>({
    filter: {},
    propertyNames: [],
  });
  const [childrenPresetItem, setChildrenPresetItem] = useState<PresetItem[]>();
  const onSelectItemsState = props.onSelectChange ?? (() => {});

  const aggregateStages = props.aggregateStagesOption?.aggregateStages ?? [];
  const aggregatedType = props.aggregateStagesOption?.aggregateType ?? '';
  const mergedShema = props.aggregateStagesOption?.mergedShema ?? false;
  const aggregateFullMethodName =
    props.aggregateStagesOption?.aggregateFullMethodName ?? [];

  useEffect(() => {
    (async () => {
      try {
        const fullMethodNames: string[] = [];
        fullMethodNames.push(props.fullMethodName);
        if (aggregateFullMethodName) {
          fullMethodNames.push(...fullMethodNames, ...aggregateFullMethodName);
        }
        const { childrenPresetItem, schemas, preset } =
          await getPresetAndSchema(props.viewId, fullMethodNames);
        // カラムの結合・読み替え処理
        const tmpSchema = schemas[0];
        if (mergedShema) {
          const schema2 = schemas[1];
          const mergedSchemas = schema2.map((v) => {
            return {
              name: `mergedContents.${v.name}`,
              typeName: v.typeName,
            };
          });
          tmpSchema.push(...mergedSchemas);
        }
        const schema = aggregatedType
          ? getSchemaByTypeName(aggregatedType)
          : tmpSchema;

        const formatSchema = props.onHandleFormatSchema ?? ((v) => v);

        setChildrenPresetItem(childrenPresetItem);
        setSchema(formatSchema(schema));
        setPreset(preset);
      } catch (err) {
        console.error(err);
        error(getExceptionMessage(intl, err));
      }
    })();
    // 初回時だけ起動させたい処理なのでlintから除外させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setModalIsOpen(props.isOpen);
    saveInitialLocalStorageData(props.viewId, myEmail);
  }, [props.isOpen]);

  const [baseModalIsOpen, setModalIsOpen] = useState(false);
  const selectModal = async () => {
    const list: DummyUser[] = [];

    const lsCheckboxValue = getLocalStorageCheckboxData(
      props.viewId ?? '',
      myEmail
    );
    const menuTarget = props.menuTarget ?? 'none';
    if (lsCheckboxValue.length >= 1) {
      // mongoDBに直接問合せ
      const result = await window.App.services.ui.worker.filter({
        action: 'query',
        fullMethodName: props.fullMethodName,
        filter: {
          [menuTarget]: { $in: lsCheckboxValue },
        },
        sort: [],
      });
      list.push(...(result.items as DummyUser[]));

      if (props.selectLimit && props.selectLimit < list.length) {
        error([msg]);
      } else {
        onSelectItemsState!(list ?? []);
      }
    } else {
      error([GetMessageWithIntl(intl, { id: 'E0000023' })]);
    }
  };

  const view = (
    <div className="content-box">
      <ListView
        pageInfo={{
          schema: schema,
          preset: preset,
          menuItem: props.menuEvent,
          menuTarget: props.menuTarget ?? 'Id',
          headerTitle: { viewId: props.viewId },
          presetItems: childrenPresetItem,
          listSkipType: {
            isTotal: true,
            isOutput: true,
            isListActionMenu: true,
            isListIconMenu: true,
          },
        }}
        isReload={false}
        fullMethodName={props.fullMethodName}
        filterItemOption={{
          aggregateStages: aggregateStages,
          isRequestBodyFilter: true,
          initialFilterItems: props.initialFilterItems ?? undefined,
          initialRequestBodyFilterItems:
            props.initialRequestBodyFilterItems ?? undefined,
        }}
        selectedItemIds={props.selectedItemIds}
      />
      <div className="btns">
        <CaptionButton
          name="cancelBtn"
          buttonType="cancel"
          className="btn limit buttonBox"
          caption={GetMessageWithIntl(intl, { id: 'cancel' })}
          onClick={() => {
            props.onCloseChange!();
          }}
        />
        <CaptionButton
          name="backBtn"
          buttonType="basic"
          className="btn limit buttonBox"
          caption={GetMessageWithIntl(intl, { id: 'select' })}
          onClick={selectModal}
        />
      </div>
    </div>
  );

  const openModalProps: ModalDialogComponentProps = {
    cancel: () => {},
    send: () => {},
    modalIsOpen: baseModalIsOpen,
    headerLabelId: props.headerLabelId,
    titleLabelId: props.headerTitleId ?? props.headerLabelId,
    messageLabelId: { prefixId: 'DIALOG_MESSAGE', viewId: props.viewId },
    elements: view,
    className: 'search-dialog',
  };

  return (
    <div className="SearchListView SearchListViewDialog">
      <ModalDialogComponent {...openModalProps} />
    </div>
  );
}
